import React, { useState } from 'react';
import { 
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CogIcon,
  TagIcon,
  CurrencyRupeeIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

const EnquiryPurposeManager = () => {
  const [activeTab, setActiveTab] = useState('categories');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  // Predefined enquiry purposes with management capabilities
  const [enquiryCategories, setEnquiryCategories] = useState({
    "Website Development": {
      icon: "🌐",
      color: "bg-blue-100 text-blue-800 border-blue-200",
      description: "Complete website solutions for businesses",
      active: true,
      subcategories: [
        { 
          id: 1,
          name: "Business Website", 
          price: "₹25,000 - ₹75,000", 
          timeline: "2-4 weeks", 
          features: ["Responsive Design", "SEO Optimized", "Contact Forms", "Admin Panel"],
          active: true
        },
        { 
          id: 2,
          name: "E-commerce Website", 
          price: "₹50,000 - ₹1,50,000", 
          timeline: "4-8 weeks", 
          features: ["Product Catalog", "Payment Gateway", "Inventory Management", "Order Tracking"],
          active: true
        },
        { 
          id: 3,
          name: "Portfolio Website", 
          price: "₹15,000 - ₹35,000", 
          timeline: "1-2 weeks", 
          features: ["Gallery", "Blog", "Contact Form", "Social Integration"],
          active: true
        }
      ]
    },
    "Mobile App Development": {
      icon: "📱",
      color: "bg-green-100 text-green-800 border-green-200",
      description: "Native and cross-platform mobile applications",
      active: true,
      subcategories: [
        { 
          id: 4,
          name: "Android App", 
          price: "₹75,000 - ₹2,50,000", 
          timeline: "8-16 weeks", 
          features: ["Native Android", "Play Store", "Push Notifications", "Offline Support"],
          active: true
        },
        { 
          id: 5,
          name: "iOS App", 
          price: "₹1,00,000 - ₹3,00,000", 
          timeline: "8-16 weeks", 
          features: ["Native iOS", "App Store", "iCloud Integration", "Apple Pay"],
          active: true
        }
      ]
    },
    "Digital Marketing": {
      icon: "📈",
      color: "bg-purple-100 text-purple-800 border-purple-200",
      description: "Complete digital marketing solutions",
      active: true,
      subcategories: [
        { 
          id: 6,
          name: "SEO Services", 
          price: "₹15,000 - ₹50,000/month", 
          timeline: "3-6 months", 
          features: ["Keyword Research", "On-page SEO", "Link Building", "Analytics"],
          active: true
        },
        { 
          id: 7,
          name: "Social Media Marketing", 
          price: "₹20,000 - ₹75,000/month", 
          timeline: "Ongoing", 
          features: ["Content Creation", "Ad Campaigns", "Community Management", "Analytics"],
          active: true
        }
      ]
    }
  });

  const handleAddCategory = () => {
    setEditingItem(null);
    setShowAddModal(true);
  };

  const handleEditCategory = (categoryName) => {
    setEditingItem({ type: 'category', data: enquiryCategories[categoryName], name: categoryName });
    setShowAddModal(true);
  };

  const handleAddSubcategory = (categoryName) => {
    setEditingItem({ type: 'subcategory', categoryName });
    setShowAddModal(true);
  };

  return (
    <div className="bg-white rounded-lg shadow-lg">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6 rounded-t-lg">
        <h2 className="text-2xl font-bold flex items-center">
          <CogIcon className="h-6 w-6 mr-2" />
          Enquiry Purpose Management
        </h2>
        <p className="text-indigo-100 mt-1">
          Manage service categories, pricing, and lead qualification options
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8 px-6">
          <button
            onClick={() => setActiveTab('categories')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'categories'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Service Categories
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'analytics'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Analytics & Reports
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'settings'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Settings
          </button>
        </nav>
      </div>

      {/* Content */}
      <div className="p-6">
        {activeTab === 'categories' && (
          <div>
            {/* Add Category Button */}
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Service Categories</h3>
              <button
                onClick={handleAddCategory}
                className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 flex items-center"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Add Category
              </button>
            </div>

            {/* Categories List */}
            <div className="space-y-6">
              {Object.entries(enquiryCategories).map(([categoryName, category]) => (
                <div key={categoryName} className={`border-2 rounded-lg p-4 ${category.color}`}>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      <span className="text-2xl mr-3">{category.icon}</span>
                      <div>
                        <h4 className="font-semibold text-lg">{categoryName}</h4>
                        <p className="text-sm opacity-80">{category.description}</p>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEditCategory(categoryName)}
                        className="p-2 hover:bg-white hover:bg-opacity-20 rounded"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleAddSubcategory(categoryName)}
                        className="p-2 hover:bg-white hover:bg-opacity-20 rounded"
                      >
                        <PlusIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  {/* Subcategories */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {category.subcategories.map((subcategory) => (
                      <div key={subcategory.id} className="bg-white bg-opacity-50 rounded-lg p-3">
                        <div className="flex items-start justify-between mb-2">
                          <h5 className="font-medium">{subcategory.name}</h5>
                          <div className="flex space-x-1">
                            <button className="p-1 hover:bg-white hover:bg-opacity-30 rounded">
                              <PencilIcon className="h-3 w-3" />
                            </button>
                            <button className="p-1 hover:bg-white hover:bg-opacity-30 rounded">
                              <TrashIcon className="h-3 w-3" />
                            </button>
                          </div>
                        </div>
                        
                        <div className="space-y-1 text-xs">
                          <div className="flex items-center">
                            <CurrencyRupeeIcon className="h-3 w-3 mr-1" />
                            {subcategory.price}
                          </div>
                          <div className="flex items-center">
                            <ClockIcon className="h-3 w-3 mr-1" />
                            {subcategory.timeline}
                          </div>
                        </div>

                        <div className="mt-2">
                          <div className="flex flex-wrap gap-1">
                            {subcategory.features.slice(0, 2).map((feature, idx) => (
                              <span key={idx} className="text-xs bg-white bg-opacity-40 px-1 py-0.5 rounded">
                                {feature}
                              </span>
                            ))}
                            {subcategory.features.length > 2 && (
                              <span className="text-xs bg-white bg-opacity-40 px-1 py-0.5 rounded">
                                +{subcategory.features.length - 2} more
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'analytics' && (
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Enquiry Analytics</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-medium text-blue-900">Most Popular Service</h4>
                <p className="text-2xl font-bold text-blue-600">Website Development</p>
                <p className="text-sm text-blue-600">45% of all enquiries</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <h4 className="font-medium text-green-900">Average Project Value</h4>
                <p className="text-2xl font-bold text-green-600">₹85,000</p>
                <p className="text-sm text-green-600">Based on last 30 days</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg">
                <h4 className="font-medium text-purple-900">Conversion Rate</h4>
                <p className="text-2xl font-bold text-purple-600">68%</p>
                <p className="text-sm text-purple-600">Enquiry to project</p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'settings' && (
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Purpose Flow Settings</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <h4 className="font-medium">Auto-assign based on purpose</h4>
                  <p className="text-sm text-gray-600">Automatically assign leads to specialists</p>
                </div>
                <input type="checkbox" className="toggle" defaultChecked />
              </div>
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <h4 className="font-medium">Show pricing in modal</h4>
                  <p className="text-sm text-gray-600">Display pricing information to customers</p>
                </div>
                <input type="checkbox" className="toggle" defaultChecked />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EnquiryPurposeManager;
